import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, concatMap, expand, last, timer } from 'rxjs';

import {
  POLLING_TIMEOUT_IN_SECONDS,
  SECONDS_IN_MILLISECONDS
} from '@core/utils/const';
import { ClientPowerBIReport } from '@power-bi/models/client-power-bi-report';
import { ExportPowerBIReport } from '@power-bi/models/export-power-bi-report';
import { ExportPowerBIReportFileDownloadFormat } from '@power-bi/models/export-power-bi-report-file-download-format';
import { ExportPowerBIReportFileFormat } from '@power-bi/models/export-power-bi-report-file-format';
import { ExportPowerBIReportOptions } from '@power-bi/models/export-power-bi-report-options';
import { ExportPowerBIReportStatus } from '@power-bi/models/export-power-bi-report-status';
import { ExportPowerBIReportStatusType } from '@power-bi/models/export-power-bi-report-status-type';
import { PowerBIExportReportService } from '@power-bi/services/power-bi-export-report.service';
import { FileSaverService } from '@shared/services/file-saver.service';
import { ToastNotificationsService } from '@shared/services/toast-notifications.service';

@Injectable()
export class PowerBiService {
  readonly #powerBIExportReportService = inject(PowerBIExportReportService);
  readonly #fileSaverService = inject(FileSaverService);
  readonly #toastNotificationsService = inject(ToastNotificationsService);
  readonly #destroyRef = inject(DestroyRef);

  exportReport(
    clientPowerBIReport: ClientPowerBIReport,
    exportOptions: ExportPowerBIReportOptions
  ): void {
    this.#powerBIExportReportService
      .startExport(clientPowerBIReport.id, exportOptions)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (exportPowerBIReport: ExportPowerBIReport) => {
          this.pollExportStatus(
            exportPowerBIReport,
            clientPowerBIReport.customName,
            exportOptions.format
          );
        },
        error: () => {
          this.#toastNotificationsService.error('Exporting report failed');
        }
      });
  }

  //#region Private methods

  private pollExportStatus(
    exportPowerBIReport: ExportPowerBIReport,
    reportName: string,
    reportFormat: ExportPowerBIReportFileFormat
  ): void {
    let waitingTime = 0;

    this.#powerBIExportReportService
      .getStatusOfExport(exportPowerBIReport)
      .pipe(
        expand((x: ExportPowerBIReportStatus) => {
          waitingTime += x.retryAfterInSeconds;

          const shouldContinuePolling =
            waitingTime < POLLING_TIMEOUT_IN_SECONDS &&
            x.status !== ExportPowerBIReportStatusType.Succeeded &&
            x.status !== ExportPowerBIReportStatusType.Failed;

          if (!shouldContinuePolling) {
            return EMPTY;
          }

          return timer(x.retryAfterInSeconds * SECONDS_IN_MILLISECONDS).pipe(
            concatMap(() =>
              this.#powerBIExportReportService.getStatusOfExport(
                exportPowerBIReport
              )
            )
          );
        }),
        takeUntilDestroyed(this.#destroyRef),
        last()
      )
      .subscribe((x: ExportPowerBIReportStatus) => {
        if (
          waitingTime >= POLLING_TIMEOUT_IN_SECONDS ||
          x.status !== ExportPowerBIReportStatusType.Succeeded
        ) {
          this.#toastNotificationsService.error('Exporting report failed');
          return;
        }

        this.downloadReport(exportPowerBIReport, reportName, reportFormat);
      });
  }

  private downloadReport(
    exportPowerBIReport: ExportPowerBIReport,
    reportName: string,
    reportFormat: ExportPowerBIReportFileFormat
  ): void {
    this.#powerBIExportReportService
      .getDownloadInfo(exportPowerBIReport)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (result: Blob) => {
          this.#toastNotificationsService.success('Report exported');

          const reportExtension =
            ExportPowerBIReportFileDownloadFormat[reportFormat];

          const blob = new Blob([result], {
            type: this.createFileType(reportFormat) ?? undefined
          });

          this.#fileSaverService.save(blob, `${reportName}.${reportExtension}`);
        },
        error: () => {
          this.#toastNotificationsService.error('Exporting report failed');
        }
      });
  }

  private createFileType(format: ExportPowerBIReportFileFormat): string | null {
    switch (format) {
      case ExportPowerBIReportFileFormat.PDF:
        return 'application/pdf';

      case ExportPowerBIReportFileFormat.PPTX:
        return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

      case ExportPowerBIReportFileFormat.PNG:
        return 'application/zip';

      default:
        return null;
    }
  }

  //#endregion
}
